import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { useSlideOver } from '@/hooks/calendar/useSlideOver';

type Props = {
  onClose: () => void;
  /** Max-width utility for the panel, e.g. 'max-w-lg' | 'max-w-xl'. */
  widthClass?: string;
  labelledBy?: string;
  /** Receives `close` so inner controls (e.g. an X button) trigger the exit animation. */
  children: (close: () => void) => ReactNode;
};

/**
 * Portaled, backdrop-dimmed slide-over panel with smooth enter + exit
 * animations. Shared chrome for the calendar's module panels.
 */
export function SlideOver({ onClose, widthClass = 'max-w-lg', labelledBy, children }: Props) {
  const { open, close } = useSlideOver(onClose);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        onClick={close}
        className={cn(
          'absolute inset-0 cursor-default bg-[#0f172b]/30 backdrop-blur-[3px] transition-opacity duration-300 ease-out',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn(
          'relative flex h-full w-full flex-col overflow-hidden rounded-l-3xl border-l border-slate-100 bg-[#F6F7FB] shadow-[-20px_0_60px_-30px_rgba(15,23,43,0.35)] transition-transform duration-300 ease-out will-change-transform dark:border-white/5 dark:bg-background',
          widthClass,
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {children(close)}
      </aside>
    </div>,
    document.body,
  );
}
